import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import {
  Outlet,
  Link,
  createRootRouteWithContext,
  useRouter,
  useRouterState,
} from "@tanstack/react-router";
import { I18nProvider } from "@project/lib/i18n";
import { Toaster } from "@project/components/ui/sonner";
import { DonorChatbot } from "@project/donate/components/donor-chatbot";
import { AuthProvider } from "@project/context/AuthContext";

function NotFoundComponent() {
  const pathname = useRouterState({ select: s => s.location.pathname });
  const isAdmin = pathname.startsWith("/admin");
  const homeTo = isAdmin ? "/admin/dashboard" : "/";
  const homeLabel = isAdmin ? "Go to Admin Dashboard" : "Go home";
  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-7xl font-bold text-foreground">404</h1>
        <h2 className="mt-4 text-xl font-semibold text-foreground">
          Page not found
        </h2>
        <p className="mt-2 text-sm text-muted-foreground">
          The page you&apos;re looking for doesn&apos;t exist or has been moved.
        </p>
        <div className="mt-6">
          <Link
            to={homeTo}
            className="hover:bg-primary/90 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors"
          >
            {homeLabel}
          </Link>
        </div>
      </div>
    </div>
  );
}

function ErrorComponent({ error, reset }: { error: Error; reset: () => void }) {
  console.error(error);
  const router = useRouter();
  const pathname = useRouterState({ select: s => s.location.pathname });
  const isAdmin = pathname.startsWith("/admin");
  const homeHref = isAdmin ? "/admin/dashboard" : "/";
  const homeLabel = isAdmin ? "Go to Admin Dashboard" : "Go home";

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4">
      <div className="max-w-md text-center">
        <h1 className="text-xl font-semibold tracking-tight text-foreground">
          This page didn&apos;t load
        </h1>
        <p className="mt-2 text-sm text-muted-foreground">
          Something went wrong on our end. You can try refreshing or head back
          home.
        </p>
        <div className="mt-6 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => {
              router.invalidate();
              reset();
            }}
            className="hover:bg-primary/90 inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground transition-colors"
          >
            Try again
          </button>
          <a
            href={homeHref}
            className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground transition-colors hover:bg-accent"
          >
            {homeLabel}
          </a>
        </div>
      </div>
    </div>
  );
}

export const Route = createRootRouteWithContext<{ queryClient: QueryClient }>()(
  {
    component: RootComponent,
    notFoundComponent: NotFoundComponent,
    errorComponent: ErrorComponent,
  }
);

function RootComponent() {
  const { queryClient } = Route.useRouteContext();
  const pathname = useRouterState({ select: s => s.location.pathname });
  // Temporarily hidden on the user-facing site. Set back to `true` to restore.
  const CHATBOT_ENABLED = false;
  const showChatbot =
    CHATBOT_ENABLED &&
    !pathname.startsWith("/admin") &&
    !pathname.startsWith("/api");

  return (
    <QueryClientProvider client={queryClient}>
      <AuthProvider>
        <I18nProvider>
          <Outlet />
          <Toaster />
          {showChatbot && <DonorChatbot />}
        </I18nProvider>
      </AuthProvider>
    </QueryClientProvider>
  );
}
